import { InputAdornment } from '@mui/material';
import {
  hasMobilePrefix,
  ProjectMobileCountry,
  toInternationalMobile,
  toLocalMobile
} from '../../../util/project-mobile-country';
import { TextField, TextFieldProps } from '../text-field/text-field.component';

export interface MobileFieldProps extends Omit<TextFieldProps, 'onChange' | 'value' | 'startAdornment'> {
  country: ProjectMobileCountry;
  // The full international number (or empty)
  value?: string | null;
  onChange(internationalMobile: string): void;
}

/**
 * Shows the country prefix as a fixed adornment and lets the user type a local number (leading zero optional).
 * The value that is emitted is always the full international number.
 *
 * If the current value has a different prefix to the one being enforced, the full international number is shown
 * (without the adornment) along with a warning so the user can see and correct it.
 */
export function MobileField({ country, value, onChange, error, helperText, ...props }: MobileFieldProps) {
  const mobile = value ?? '';
  const isMismatched = !hasMobilePrefix(mobile, country);

  return (
    <TextField
      {...props}
      type="tel"
      shrinkLabel={isMismatched ? undefined : true}
      value={isMismatched ? mobile : toLocalMobile(mobile, country)}
      onChange={(event) => onChange(toInternationalMobile(event.target.value, country))}
      error={error || isMismatched}
      helperText={
        isMismatched
          ? `This number doesn't start with ${country.prefix} (${country.name}). Enter a mobile number for ${country.name} to continue.`
          : helperText
      }
      startAdornment={
        isMismatched ? undefined : (
          <InputAdornment position="start" data-testid="mobile-prefix">
            {country.prefix}
          </InputAdornment>
        )
      }
    />
  );
}
